import { PropsWithChildren, useCallback, useMemo } from "react";
import React from "react";
import {
  INFERENCE_TYPE_LABELS,
  INFERENCE_TYPE_WITH_SECOND_REFERENCE_IMAGE,
  InferenceType,
  InferenceWithIpAdapterType,
} from "domains/inference/constants/inferenceTypes";
import { useInferenceCreateContext } from "domains/inference/contexts/InferenceProvider";
import Select from "domains/ui/components/Select";
import WithLabelAndTooltip from "domains/ui/components/WithLabelAndTooltip";
import { useUserContext } from "domains/user/contexts/UserProvider";
import { renderToStaticMarkup } from "react-dom/server";

export default function SelectInferenceType() {
  const {
    model,
    inferenceType,
    setInferenceType,
    isSdxl,
    isInCanvas,
    mask,
    ipAdapterType,
    setIpAdapterType,
    isTexture,
  } = useInferenceCreateContext();

  const { featureFlags } = useUserContext();
  const hasIpAdapterFlag = featureFlags.includes("ip-adapter");

  const guide = useMemo(() => {
    if (inferenceType === "img2img") {
      return "inference-parameter-reference-image-mode-image2image";
    } else if (inferenceType === "controlnet") {
      return "inference-parameter-reference-image-mode-controlnet";
    } else if (
      inferenceType === "txt2img_ip_adapter" &&
      ipAdapterType === "character"
    ) {
      return "inference-parameter-reference-image-mode-character-reference";
    } else if (
      inferenceType === "txt2img_ip_adapter" &&
      ipAdapterType === "style"
    ) {
      return "inference-parameter-reference-image-mode-style-reference";
    } else if (
      INFERENCE_TYPE_WITH_SECOND_REFERENCE_IMAGE.includes(inferenceType)
    ) {
      return "inference-parameter-reference-image-mode-dual";
    }
    return undefined;
  }, [inferenceType, ipAdapterType]);

  const handleChange = useCallback(
    (e: React.ChangeEvent<HTMLSelectElement>) => {
      const value = e.target.value;
      if (value.includes("_ip_adapter_")) {
        const [type, subtype] = value.split("_ip_adapter_");
        setInferenceType(`${type}_ip_adapter` as InferenceType);
        setIpAdapterType(subtype as typeof ipAdapterType);
      } else {
        setInferenceType(value as InferenceType);
      }
    },
    [setInferenceType, setIpAdapterType]
  );

  const inferenceTypeForSelect = useMemo(
    () =>
      inferenceType.endsWith("_ip_adapter") && ipAdapterType
        ? `${inferenceType}_${ipAdapterType}`
        : inferenceType,
    [inferenceType, ipAdapterType]
  );

  // Disable controlnet when using SDXL model with mask and inpaint in canvas as api don't support it
  const isControlNetDisabled = !!(isSdxl && isInCanvas && mask);

  const modelCapabilities = useMemo(() => model?.capabilities, [model]);

  return (
    <WithLabelAndTooltip label="Mode" guide={guide}>
      <Select
        w="fit-content"
        mr={-1}
        ml="auto"
        onChange={handleChange}
        value={inferenceTypeForSelect}
      >
        {isTexture && (
          <>
            <OptionItem value="img2img_texture" />
            <OptionItem value="controlnet_texture" label="Structure" />
          </>
        )}
        {isInCanvas && (
          <>
            <OptionItem value="img2img" />
            <OptionItem value={"controlnet"} disabled={isControlNetDisabled} />
          </>
        )}
        {!isInCanvas && !isTexture && (
          <>
            <OptionItem disabled>— Single Image Control —</OptionItem>
            <OptionItem modelCapabilities={modelCapabilities} value="img2img" />
            <OptionItem
              modelCapabilities={modelCapabilities}
              value={"controlnet"}
            />
            {hasIpAdapterFlag && (
              <>
                <OptionItem
                  modelCapabilities={modelCapabilities}
                  value="txt2img_ip_adapter_character"
                />
                <OptionItem
                  modelCapabilities={modelCapabilities}
                  value="txt2img_ip_adapter_style"
                />
              </>
            )}

            <DualReference>
              <OptionItem
                modelCapabilities={modelCapabilities}
                value="controlnet_img2img"
              />

              {hasIpAdapterFlag && (
                <>
                  <OptionItem
                    modelCapabilities={modelCapabilities}
                    value="img2img_ip_adapter_character"
                  />
                  <OptionItem
                    modelCapabilities={modelCapabilities}
                    value="img2img_ip_adapter_style"
                  />
                  <OptionItem
                    modelCapabilities={modelCapabilities}
                    value="controlnet_ip_adapter_character"
                  />
                  <OptionItem
                    modelCapabilities={modelCapabilities}
                    value="controlnet_ip_adapter_style"
                  />
                </>
              )}
            </DualReference>
          </>
        )}
      </Select>
    </WithLabelAndTooltip>
  );
}

// ------------------------------------

const DualReference = React.memo(function DualReference({
  children,
}: PropsWithChildren) {
  const markup = renderToStaticMarkup(<>{children}</>);
  if (!markup.trim()) {
    return null;
  }

  return (
    <>
      <OptionItem disabled>— Dual Image Control —</OptionItem>

      {children}
    </>
  );
});

// ------------------------------------

interface OptionItemProps
  extends Omit<React.HTMLProps<HTMLOptionElement>, "value"> {
  modelCapabilities?: string[];
  value?: InferenceWithIpAdapterType;
}

const OptionItem = React.memo(function OptionItem({
  modelCapabilities,
  value,
  label,
  ...props
}: OptionItemProps) {
  const capabilityValue =
    value && value.includes("_ip_adapter_")
      ? value.split("_ip_adapter_")[0] + "_ip_adapter"
      : value;
  if (
    capabilityValue &&
    modelCapabilities?.length &&
    !modelCapabilities.includes(capabilityValue)
  ) {
    return null;
  }

  return (
    <option
      value={value}
      label={label ?? INFERENCE_TYPE_LABELS[value!]}
      {...props}
    />
  );
});
